import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetGenericAccountParticipantByIdMutation } from "../../../api/services/client/clientApis";
import { GenericAccountParticipantResponse } from "../../../api/services/client/models";
import { MuiDoubleLabelWithFormattedPhoneText } from "../../../components";
import { getIsoMapCountryCode } from "../../../components/phoneLibrary/rawCountries";
import {
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../helpers/constants";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getValueOrBlank,
  isNotEmpty,
} from "../../../helpers/utils";
import { usePermissions } from "../../../hooks/usePermissions";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../redux/slices/appState/appStateSelector";
import { createGenericAccounts } from "../../genericAccounts/createGenericAccount/siteCore/SiteCoreKeys";
import { GENERIC_ACCOUNT_TAB_INDEX } from "../../genericAccounts/editGenericAccountTab/EditGenericAccountTab";
import { EditViewParticipantComponent } from "../components/editViewParticipantComponent/EditViewParticipantComponent";
import {
  EmailNotifications,
  ParticipantDetails,
} from "../components/modelsAndSchemas/Models";
import {
  getParticipantPreferredPhoneNumber,
  getSelectedContactPreferences,
  getSelectedEmail,
  getSelectedLanguagePreference,
} from "../helper";

const ViewParticipant: FC = () => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const navigate = useNavigate();
  const [participantData, setParticipantData] = useState<ParticipantDetails>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const location = useLocation();

  const { hasModulePermission } = usePermissions();

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const participantId = location?.state?.candidateId;
  const from = location?.state?.from;
  const genericAccountId = location?.state?.genericAccountId;
  const isFromViewParticipant = location?.state?.isFromViewParticipant;

  const { mutateAsync: getParticipantData, data } =
    useGetGenericAccountParticipantByIdMutation();

  useEffect(() => {
    if (participantId) getParticipantData(participantId);
  }, [participantId]);

  const handleEditClick = () => {
    navigate(PAGE_URLS.EDIT_PARTICIPANT, {
      replace: isNotEmpty(from),
      state: {
        candidateId: participantId,
        from: from,
        isFromViewParticipant: isFromViewParticipant,
        genericAccountId: genericAccountId,
      },
    });
  };

  const navigateToEditGenericAccount = () => {
    navigate(PAGE_URLS.EDIT_GENERIC_ACCOUNTS, {
      replace: true,
      state: {
        genericAccountId: genericAccountId,
        tabIndex: GENERIC_ACCOUNT_TAB_INDEX.RegisteredParticipants,
      },
    });
  };

  const navigateToViewGenericAccountPage = () => {
    navigate(PAGE_URLS.VIEW_GENERIC_ACCOUNT, {
      replace: true,
      state: {
        genericAccountId: genericAccountId,
        tabIndex: GENERIC_ACCOUNT_TAB_INDEX.RegisteredParticipants,
      },
    });
  };

  const handleBackNavigation = () => {
    if (from === PAGE_IDS.REGISTERED_PARTICIPANT_TAB) {
      if (isFromViewParticipant) navigateToViewGenericAccountPage();
      else navigateToEditGenericAccount();
    } else {
      navigate(-1);
    }
  };

  const getLabel = (key: keyof typeof createGenericAccounts) => {
    return fetchLabel(
      createGenericAccounts.registeredParticipantsTitle,
      createGenericAccounts[key],
    );
  };

  const getContactPreferencesLabel = (key: string) => {
    return fetchLabel(createGenericAccounts.contactPreferencesTitle, key);
  };

  const getParticipantInformationLabel = (key: string) => {
    return fetchLabel(createGenericAccounts.participantInformationTitle, key);
  };

  const renderFormattedPreferredPhoneNumber = (
    label: string,
    data: GenericAccountParticipantResponse,
  ) => {
    return (
      <MuiDoubleLabelWithFormattedPhoneText
        label={label}
        replaceEmptyWithDash
        selectedCountry={getIsoMapCountryCode(
          data?.participantInformation?.preferredContactNumberISOCode,
        )}
        phoneNumber={data?.participantInformation?.preferredContactNumber}
        countryCode={
          data?.participantInformation?.preferredContactNumberISOCode
        }
      />
    );
  };

  useEffect(() => {
    if (isNotEmpty(data) && !siteCoreLoading) {
      setParticipantData({
        participantInformation: {
          details: [
            {
              label: getParticipantInformationLabel(
                createGenericAccounts.participantInformationFirstName,
              ),
              value: data?.participantInformation?.firstName,
            },
            {
              label: getParticipantInformationLabel(
                createGenericAccounts.participantInformationLastName,
              ),
              value: data?.participantInformation?.lastName,
            },
            {
              label: getParticipantInformationLabel(
                createGenericAccounts.participantInformationPreferredName,
              ),
              value: data?.participantInformation?.preferredName,
            },
            {
              label: getParticipantInformationLabel(
                createGenericAccounts.participantInformationEmail,
              ),
              value: data?.participantInformation?.email,
            },
            {
              label: getParticipantInformationLabel(
                createGenericAccounts.participantInformationAlternateEmail,
              ),
              value: data?.participantInformation?.alternateEmail,
            },
            {
              label: getParticipantInformationLabel(
                createGenericAccounts.participantInformationPreferredNumber,
              ),
              value: getParticipantPreferredPhoneNumber(data),
              isEdit: true,
              render: () =>
                renderFormattedPreferredPhoneNumber(
                  getParticipantInformationLabel(
                    createGenericAccounts.participantInformationPreferredNumber,
                  ),
                  data,
                ),
            },
          ],
          hasPermission: hasModulePermission(
            MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_PARTICIPANT_INFORMATION,
          ),
        },
        programInformation: {
          details: [
            {
              label: fetchLabel(
                createGenericAccounts.programInformationTitle,
                createGenericAccounts.programInformationPlatformPersona,
              ),
              value: data?.programInformation?.platformPersona,
            },
            {
              label: fetchLabel(
                createGenericAccounts.programInformationTitle,
                createGenericAccounts.programInformationJobLanded,
              ),
              value: data?.programInformation?.isJobLanded
                ? commonLabels.yes
                : commonLabels.no,
            },
            {
              label: fetchLabel(
                createGenericAccounts.programInformationTitle,
                createGenericAccounts.programInformationProgramEndDate,
              ),
              value: data?.programInformation?.programEndDate
                ? convertToUserFormat(
                    data?.programInformation?.programEndDate,
                    userCountryDateFormat,
                  )
                : "",
            },
          ],
          hasPermission: hasModulePermission(
            MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_PROGRAM_INFORMATION,
          ),
        },
        platformDetails: {
          details: [
            {
              label: fetchLabel(
                createGenericAccounts.platformDetailsTitle,
                createGenericAccounts.platformDetailsTimeZone,
              ),
              value: getValueOrBlank(data?.platformDetails?.timeZone?.name),
            },
            {
              label: fetchLabel(
                createGenericAccounts.platformDetailsTitle,
                createGenericAccounts.platformDetailsCityOfResidence,
              ),
              value: data?.platformDetails?.city,
            },
            {
              label: fetchLabel(
                createGenericAccounts.platformDetailsTitle,
                createGenericAccounts.platformDetailsLanguagePreference,
              ),
              value: getSelectedLanguagePreference(
                data?.platformDetails?.languagePreferences,
              )?.name,
            },
          ],
          hasPermission: hasModulePermission(
            MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_PLATFORM_DETAILS,
          ),
        },
        contactPreferences: {
          details: [
            ...(data?.contactPreferences
              ? [
                  {
                    label: getContactPreferencesLabel(
                      createGenericAccounts.selectedPreference,
                    ),
                    value: getSelectedContactPreferences(
                      data?.contactPreferences,
                      getContactPreferencesLabel,
                    ),
                  },
                ]
              : []),
            ...(isNotEmpty(data?.participantInformation?.preferredContactNumber)
              ? [
                  {
                    label: getContactPreferencesLabel(
                      createGenericAccounts.preferredTextSMSNotifications,
                    ),
                    value: getParticipantPreferredPhoneNumber(data),
                    isEdit: true,
                    render: () =>
                      renderFormattedPreferredPhoneNumber(
                        getContactPreferencesLabel(
                          createGenericAccounts.preferredTextSMSNotifications,
                        ),
                        data,
                      ),
                  },
                ]
              : []),
            {
              label: getContactPreferencesLabel(
                createGenericAccounts.preferredEmailNotifications,
              ),
              value: getSelectedEmail(
                data?.contactPreferences
                  ?.emailforNotifications as EmailNotifications[],
              ),
            },
          ],
          hasPermission: hasModulePermission(
            MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_CONTACT_PREFERENCES,
          ),
        },
      });
    }
  }, [data, siteCoreLoading]);

  return (
    <EditViewParticipantComponent
      participantData={participantData}
      headerTitle={getLabel("viewParticipant")}
      primaryButtonTitle={
        hasModulePermission(MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT)
          ? commonLabels.edit
          : ""
      }
      secondaryButtonTitle={commonLabels.close}
      showEditButtonInHeader={true}
      onHeaderEditButtonClick={handleEditClick}
      onPrimaryButtonClick={handleEditClick}
      onSecondaryButtonClick={handleBackNavigation}
      participantId={getValueOrBlank(data?.participantId)}
    />
  );
};

export default ViewParticipant;
